import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import RangeSlider from '../molecules/RangeSlider'
import SelectDropdown from '../molecules/SelectDropdown'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import { setInput, setSize, setForegroundColor, setBackgroundColor, setErrorCorrectionLevel, clearQrState } from '../store/slices/qrSlice'
import { useDebounce } from '../hooks/useDebounce'
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react'
import { buildQrValue, QR_TYPES, defaultFields } from '../utils/qrBuilders'
import './QrGeneratorPage.css'

const QrGeneratorPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { input, size, foregroundColor, backgroundColor, errorCorrectionLevel } = useSelector(state => state.qr)
  const qrRef = useRef(null)
  const debouncedInput = useDebounce(input, 300)

  const [qrType, setQrType] = useState('url')
  const [fields, setFields] = useState(defaultFields)
  const currentFields = fields[qrType]
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  const qrValue = buildQrValue(qrType, currentFields)

  useEffect(() => {
    dispatch(setInput(qrValue))
  }, [qrValue, dispatch])

  const handleFieldChange = (name, value) => {
    setFields(prev => ({
      ...prev,
      [qrType]: { ...prev[qrType], [name]: value }
    }))
  }

  const handleSizeChange = (e) => {
    dispatch(setSize(parseInt(e.target.value)))
  }

  const handleForegroundColorChange = (e) => {
    dispatch(setForegroundColor(e.target.value))
  }

  const handleBackgroundColorChange = (e) => {
    dispatch(setBackgroundColor(e.target.value))
  }

  const handleErrorCorrectionChange = (e) => {
    dispatch(setErrorCorrectionLevel(e.target.value))
  }

  const handleDownload = (format = 'png') => {
    if (!input) return

    if (format === 'png') {
      const canvas = qrRef.current.querySelector('canvas')
      if (canvas) {
        const url = canvas.toDataURL('image/png')
        const a = document.createElement('a')
        a.href = url
        a.download = 'qrcode.png'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } else if (format === 'svg') {
      const svgContainer = document.createElement('div')
      document.body.appendChild(svgContainer)

      const svgRoot = ReactDOM.createRoot(svgContainer)
      svgRoot.render(
        <QRCodeSVG
          value={input}
          size={size}
          fgColor={foregroundColor}
          bgColor={backgroundColor}
          level={errorCorrectionLevel}
          includeMargin={true}
        />
      )

      setTimeout(() => {
        const svg = svgContainer.querySelector('svg')
        if (svg) {
          const serializer = new XMLSerializer()
          const svgString = serializer.serializeToString(svg)
          const blob = new Blob([svgString], { type: 'image/svg+xml' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'qrcode.svg'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
        document.body.removeChild(svgContainer)
      }, 100)
    }
  }

  const handleClear = () => {
    setFields(prev => ({ ...prev, [qrType]: defaultFields[qrType] }))
    dispatch(clearQrState())
  }

  const errorCorrectionOptions = [
    { value: 'L', label: 'Low (7%)' },
    { value: 'M', label: 'Medium (15%)' },
    { value: 'Q', label: 'Quartile (25%)' },
    { value: 'H', label: 'High (30%)' },
  ]

  return (
    <ToolPageTemplate path="/qr-generator/" tools={tools}>
      <div>
        <section className="qr-generator">
          <div className="qr-input-section">
            <div className="qr-type-tabs">
              {QR_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  className={`qr-type-tab ${qrType === t.value ? 'active' : ''}`}
                  onClick={() => setQrType(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="qr-fields">
              {qrType === 'url' && (
                <Input label="Website URL" value={currentFields.url}
                  onChange={(e) => handleFieldChange('url', e.target.value)}
                  placeholder="example.com or https://example.com" />
              )}

              {qrType === 'text' && (
                <Input label="Text" value={currentFields.text}
                  onChange={(e) => handleFieldChange('text', e.target.value)}
                  placeholder="Enter any text to encode" />
              )}

              {qrType === 'wifi' && (
                <>
                  <Input label="Network Name (SSID)" value={currentFields.ssid}
                    onChange={(e) => handleFieldChange('ssid', e.target.value)}
                    placeholder="MyWiFiNetwork" />
                  {currentFields.encryption !== 'nopass' && (
                    <Input label="Password" value={currentFields.password}
                      onChange={(e) => handleFieldChange('password', e.target.value)}
                      placeholder="WiFi password" />
                  )}
                  <SelectDropdown label="Encryption" value={currentFields.encryption}
                    onChange={(e) => handleFieldChange('encryption', e.target.value)}
                    options={[
                      { value: 'WPA', label: 'WPA/WPA2' },
                      { value: 'WEP', label: 'WEP' },
                      { value: 'nopass', label: 'No Password' },
                    ]} />
                  <label className="qr-checkbox">
                    <input type="checkbox" checked={currentFields.hidden}
                      onChange={(e) => handleFieldChange('hidden', e.target.checked)} />
                    <span>Hidden network</span>
                  </label>
                </>
              )}

              {qrType === 'vcard' && (
                <>
                  <div className="qr-field-row">
                    <Input label="First Name" value={currentFields.firstName}
                      onChange={(e) => handleFieldChange('firstName', e.target.value)}
                      placeholder="John" />
                    <Input label="Last Name" value={currentFields.lastName}
                      onChange={(e) => handleFieldChange('lastName', e.target.value)}
                      placeholder="Doe" />
                  </div>
                  <Input label="Phone" value={currentFields.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    placeholder="+1234567890" />
                  <Input label="Email" value={currentFields.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder="john@example.com" />
                  <div className="qr-field-row">
                    <Input label="Organization" value={currentFields.org}
                      onChange={(e) => handleFieldChange('org', e.target.value)}
                      placeholder="Company Inc." />
                    <Input label="Job Title" value={currentFields.title}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      placeholder="Manager" />
                  </div>
                  <Input label="Website" value={currentFields.url}
                    onChange={(e) => handleFieldChange('url', e.target.value)}
                    placeholder="https://example.com" />
                </>
              )}

              {qrType === 'email' && (
                <>
                  <Input label="To" value={currentFields.to}
                    onChange={(e) => handleFieldChange('to', e.target.value)}
                    placeholder="hello@example.com" />
                  <Input label="Subject" value={currentFields.subject}
                    onChange={(e) => handleFieldChange('subject', e.target.value)}
                    placeholder="Email subject" />
                  <Input label="Message" value={currentFields.body}
                    onChange={(e) => handleFieldChange('body', e.target.value)}
                    placeholder="Email body" />
                </>
              )}

              {qrType === 'phone' && (
                <Input label="Phone Number" value={currentFields.number}
                  onChange={(e) => handleFieldChange('number', e.target.value)}
                  placeholder="+1234567890" />
              )}

              {qrType === 'sms' && (
                <>
                  <Input label="Phone Number" value={currentFields.number}
                    onChange={(e) => handleFieldChange('number', e.target.value)}
                    placeholder="+1234567890" />
                  <Input label="Message" value={currentFields.message}
                    onChange={(e) => handleFieldChange('message', e.target.value)}
                    placeholder="Your message" />
                </>
              )}

              {qrType === 'geo' && (
                <div className="qr-field-row">
                  <Input label="Latitude" value={currentFields.lat}
                    onChange={(e) => handleFieldChange('lat', e.target.value)}
                    placeholder="37.7749" />
                  <Input label="Longitude" value={currentFields.lng}
                    onChange={(e) => handleFieldChange('lng', e.target.value)}
                    placeholder="-122.4194" />
                </div>
              )}
            </div>

            <div className="qr-controls">
              <div className="control-grid">
                <RangeSlider
                  label="Size"
                  value={size}
                  onChange={handleSizeChange}
                  min={100}
                  max={500}
                  step={10}
                  unit="px"
                />

                <div className="color-controls">
                  <div className="color-input">
                    <label>Foreground Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={foregroundColor}
                        onChange={handleForegroundColorChange}
                      />
                      <span>{foregroundColor}</span>
                    </div>
                  </div>

                  <div className="color-input">
                    <label>Background Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={handleBackgroundColorChange}
                      />
                      <span>{backgroundColor}</span>
                    </div>
                  </div>
                </div>

                <SelectDropdown
                  label="Error Correction"
                  value={errorCorrectionLevel}
                  onChange={handleErrorCorrectionChange}
                  options={errorCorrectionOptions}
                />
              </div>

              <div className="qr-buttons">
                <Button variant="secondary" onClick={handleClear}>
                  Clear
                </Button>
              </div>
            </div>
          </div>

          {debouncedInput && (
            <ResultBox title="Generated QR Code">
              <div className="qr-result">
                <div className="qr-preview" ref={qrRef}>
                  <QRCodeCanvas
                    value={debouncedInput}
                    size={size}
                    fgColor={foregroundColor}
                    bgColor={backgroundColor}
                    level={errorCorrectionLevel}
                    includeMargin={true}
                  />
                </div>

                <div className="qr-download-buttons">
                  <Button onClick={() => handleDownload('png')}>
                    Download PNG
                  </Button>
                  <Button variant="secondary" onClick={() => handleDownload('svg')}>
                    Download SVG
                  </Button>
                </div>
              </div>
            </ResultBox>
          )}
        </section>

        <section className="tool-guide">
          {/* ENHANCED: Definition with more technical depth */}
          <div className="guide-section">
            <h2>What Is a QR Code?</h2>
            <p>A QR (Quick Response) code is a two-dimensional barcode that stores data as a grid of black and white squares. Denso Wave, a Japanese automotive subsidiary, invented it in 1994 for tracking parts on a factory line — the "Quick Response" name came from the speed at which early scanners could read it. Denso Wave chose not to exercise its patent rights on the technology, which is a large part of why QR codes became universal.</p>
            <p>The format is standardized as ISO/IEC 18004. A QR code contains three types of elements: <strong>finder patterns</strong> (the three large squares in the corners that let a scanner detect position and angle), <strong>timing patterns</strong> (the dotted lines that let the scanner map coordinates), and the <strong>data region</strong> itself. About 30% of the code is reserved for error correction — redundancy that lets the code remain readable even when partially damaged, smudged, or covered.</p>
            <p>A QR code simply encodes text. What makes it feel "smart" is that phones recognize the format of that text automatically: a string starting with <code>https://</code> opens a browser, <code>mailto:</code> opens the mail app, <code>WIFI:</code> connects to a network, and a vCard block adds a contact. This tool generates all of those formats from a single input field — you pick the type, and it constructs the correctly formatted text behind the scenes.</p>
          </div>

          {/* ENHANCED: How it works — simpler language, more technical accuracy */}
          <div className="guide-section">
            <h2>How QR Code Generation Works</h2>
            <p>When you type data into the input field, this tool runs the ISO/IEC 18004 encoding pipeline: it analyzes the input, chooses the most compact encoding scheme (numeric, alphanumeric, byte, or Kanji), converts the data to a bit stream, adds error correction codes based on the level you selected, and arranges the resulting bits into the QR pattern. Every one of those steps runs in your browser — the QR you see on screen was generated locally on your device, not on a server.</p>
            <p>Higher error correction levels add more redundant data, which makes the code more resilient but also denser. A denser code is harder to scan from far away or at a shallow angle, so error correction is a trade-off: use <strong>Level M (15%)</strong> for most print applications, <strong>Level Q (25%)</strong> for outdoor signage where the code may get partially obscured, and <strong>Level H (30%)</strong> only when you need the code to survive a logo or image overlay in the center.</p>
            <p>You can download your QR as either PNG or SVG. PNG is a raster format — perfect for social media, email, and screen display. SVG is a vector format that scales to any size without quality loss, which makes it the right choice for anything printed (business cards, signage, product packaging). Both are generated entirely from your input with no watermark and no expiration.</p>
          </div>

          {/* EXISTING: QR Code Types (kept — very good) */}
          <div className="guide-section">
            <h2>QR Code Types You Can Create</h2>
            <p>A QR code simply encodes whatever text you give it. That means you can create many specialized QR code types with this tool just by pasting the right formatted text into the input field above. Your phone's camera automatically recognizes the format and offers the matching action (connect to WiFi, save a contact, open email, and so on). Here are the most useful formats:</p>
            <div className="qr-types">
              <div className="qr-type">
                <h3>Website or Link QR Code</h3>
                <p>Send people straight to any web page. Always include the full protocol so the link opens reliably.</p>
                <code className="qr-type-format">https://example.com</code>
              </div>
              <div className="qr-type">
                <h3>WiFi QR Code</h3>
                <p>Let guests connect to your network by scanning, without typing the password. Replace the network name and password.</p>
                <code className="qr-type-format">WIFI:T:WPA;S:MyNetwork;P:MyPassword;;</code>
              </div>
              <div className="qr-type">
                <h3>Contact / vCard QR Code</h3>
                <p>Share your contact details so people can save you to their phone in one scan. Perfect for business cards.</p>
                <code className="qr-type-format">BEGIN:VCARD&#10;VERSION:3.0&#10;FN:John Doe&#10;TEL:+1234567890&#10;EMAIL:john@example.com&#10;END:VCARD</code>
              </div>
              <div className="qr-type">
                <h3>Email QR Code</h3>
                <p>Open a pre-filled email to your address. You can also add a subject and body after the address.</p>
                <code className="qr-type-format">mailto:hello@example.com</code>
              </div>
              <div className="qr-type">
                <h3>Phone Number QR Code</h3>
                <p>Start a phone call to the number as soon as the code is scanned. Great for posters and flyers.</p>
                <code className="qr-type-format">tel:+1234567890</code>
              </div>
              <div className="qr-type">
                <h3>SMS QR Code</h3>
                <p>Open a text message to a number with an optional pre-written message ready to send.</p>
                <code className="qr-type-format">SMSTO:+1234567890:Your message here</code>
              </div>
              <div className="qr-type">
                <h3>Plain Text QR Code</h3>
                <p>Store any text such as a note, serial number, WiFi tip, or short message. Just type it in directly.</p>
                <code className="qr-type-format">Any text you want to store</code>
              </div>
              <div className="qr-type">
                <h3>Location QR Code</h3>
                <p>Point people to a map location using latitude and longitude coordinates.</p>
                <code className="qr-type-format">geo:37.7749,-122.4194</code>
              </div>
            </div>
          </div>

          {/* EXISTING: How to Use (kept) */}
          <div className="guide-section">
            <h2>How to Use This QR Generator</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Enter your content</h3>
                  <p>Type or paste your content into the input field. For URLs, include the full protocol (https://) for best compatibility. For specialized types (WiFi, vCard, email), use the tabs above to fill in the fields — the tool builds the correctly formatted string for you.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Customize the appearance</h3>
                  <p>Adjust the size using the slider (100–500px) and choose foreground and background colors that match your brand. Ensure high contrast for reliable scanning — dark foreground on light background works best.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Set the error correction level</h3>
                  <p>Choose based on your use case: Low (7%) for clean, controlled environments. Medium (15%) for general use. Quartile (25%) for outdoor signage. High (30%) for codes that might get damaged or partially covered.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Download PNG or SVG</h3>
                  <p>Choose PNG for digital use (social media, email, screen) or SVG for print (business cards, posters, product packaging). The QR code regenerates as you type — every change is visible in real time.</p>
                </div>
              </div>
            </div>
          </div>

          {/* EXISTING: Use Cases (kept, tightened) */}
          <div className="guide-section">
            <h2>Common Use Cases</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>Business cards and networking</h3>
                <p>Add a QR code to the back of a business card that saves your full contact details to the scanner's phone in one tap — no typing, no typos, no lost cards.</p>
              </div>
              <div className="use-case">
                <h3>Restaurant menus and ordering</h3>
                <p>Table tents with a QR code to your menu page let customers browse and order without an app or paper menu. Still the most common QR use case post-2020.</p>
              </div>
              <div class="use-case">
                <h3>WiFi guest access</h3>
                <p>Print one QR code for your guest network and let visitors connect without asking for the password. Especially useful in cafés, offices, and rentals.</p>
              </div>
              <div className="use-case">
                <h3>Event management</h3>
                <p>Ticketing, check-in, schedules, and venue maps — each attendee scans the code that matches their need. Print once, works forever.</p>
              </div>
              <div className="use-case">
                <h3>Product packaging</h3>
                <p>Link a printed code to a product manual, warranty registration page, or tutorial video. Physical product, digital destination.</p>
              </div>
              <div className="use-case">
                <h3>Educational materials</h3>
                <p>Textbooks and worksheets with a QR code to supplementary videos, exercises, or resources. Teachers can update the destination without reprinting.</p>
              </div>
            </div>
          </div>

          {/* EXISTING: Static vs Dynamic (kept — excellent content) */}
          <div className="guide-section">
            <h2>Static vs Dynamic QR Codes</h2>
            <p>When choosing a QR code, one of the most important decisions is whether you need a static or dynamic code. Understanding the difference helps you pick the right tool for your project and avoid paying for features you don't need.</p>
            <div className="comparison-grid">
              <div className="comparison-card">
                <h3>Static QR Codes</h3>
                <p>A static QR code stores the data directly inside the pattern itself. The information is fixed at the moment you create it and the code works forever, with no server, account, or subscription in the loop.</p>
                <ul>
                  <li>Never expire and keep working indefinitely</li>
                  <li>Completely free with no monthly limits</li>
                  <li>Work fully offline, nothing can switch them off</li>
                  <li>More private, since no data passes through a third party</li>
                  <li>Cannot be edited after printing and offer no scan tracking</li>
                </ul>
                <p className="comparison-note">This is exactly what our free generator creates.</p>
              </div>
              <div className="comparison-card">
                <h3>Dynamic QR Codes</h3>
                <p>A dynamic QR code stores a short redirect link that points to a destination you can change later. The code stays the same while the destination is managed on a provider's server.</p>
                <ul>
                  <li>Editable destination after the code is printed</li>
                  <li>Built-in scan analytics and tracking</li>
                  <li>Shorter, less dense pattern that scans faster</li>
                  <li>Usually require a paid account or subscription</li>
                  <li>Stop working if the provider shuts down or the plan lapses</li>
                </ul>
                <p className="comparison-note">Best for marketing campaigns that need editing and tracking.</p>
              </div>
            </div>
            <p>For most personal and small-business needs, such as business cards, WiFi sharing, menus, and event signage, a static QR code is the smarter choice. It is free, permanent, and reliable. Choose a dynamic code only when you genuinely need to edit the destination later or measure scan performance.</p>
          </div>

          {/* NEW: Best practices for scanning reliability */}
          <div className="guide-section">
            <h2>QR Code Best Practices for Reliable Scanning</h2>
            <p>Getting a QR code to scan reliably comes down to a handful of physical-design decisions. These are the ones that cause the most real-world problems:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Minimum size</h3>
                <p>Print QR codes at least 2×2 cm (roughly 0.8 inches) square. Anything smaller is hard for a phone camera to resolve from a comfortable scanning distance. For a code meant to be scanned from 1 meter away, use at least 3–4 cm.</p>
              </div>
              <div className="use-case">
                <h3>Contrast and background</h3>
                <p>Dark code on a light background scans best. Avoid light-on-dark codes (a light code on dark background) — most scanners can handle them, but reliability drops, especially in low light. Never use low-contrast color pairs like light gray on white.</p>
              </div>
              <div className="use-case">
                <h3>Quiet zone (white border)</h3>
                <p>Leave a clear margin of at least 4 modules (roughly the width of one finder pattern) on all sides. The scanner uses this white border to detect the code's edges. A QR code printed right up against text or a busy background often fails to scan even though the code itself is fine.</p>
              </div>
              <div className="use-case">
                <h3>Print quality and surface</h3>
                <p>Scan reliability drops sharply on curved, reflective, or textured surfaces — curved mugs, glossy stickers, textured labels. If you must print on those surfaces, increase the size and use Level Q or H error correction to compensate.</p>
              </div>
              <div className="use-case">
                <h3>Test before mass printing</h3>
                <p>Scan your QR code with at least two different phones before committing to a print run. Camera apps differ in their sensitivity, and a code that scans fine on one device may struggle on another — especially in poor lighting or at an angle.</p>
              </div>
              <div className="use-case">
                <h3>Destination URL best practices</h3>
                <p>Use a short, stable URL rather than a long query string. Long URLs create a denser QR pattern that's harder to scan. Also avoid URLs that depend on country-specific redirects unless you've tested them from multiple locations.</p>
              </div>
            </div>
          </div>

          {/* NEW: Common QR code mistakes */}
          <div className="guide-section">
            <h2>Common QR Code Mistakes</h2>
            <p><strong>Printing a code that hasn't been tested.</strong> The single biggest QR failure mode is a code that looks fine on screen but fails to scan in the real world because of size, contrast, or placement. Test every code with at least two phones before printing more than one copy.</p>
            <p><strong>Over-branding the code with a centered logo.</strong> A centered logo covers data modules. It's only safe with Level H error correction (30% redundancy), and even then the logo must stay small — under roughly 25% of the code's area. Larger logos break scanning.</p>
            <p><strong>Using a dynamic code from a provider that disappears.</strong> Dynamic QR codes are tied to the provider's redirect server. If the provider shuts down, changes their pricing, or you forget to renew, every printed code stops working. For permanent use — business cards, signage, product labels — a static code (like the ones this tool generates) is safer.</p>
            <p><strong>Placing a code where there's no signal or lighting.</strong> QR codes on the outside of buildings, on underground signage, or in areas with no WiFi coverage often can't deliver their destination to the user. Design the surrounding context as carefully as the code itself.</p>
            <p><strong>Assuming a short URL is always better.</strong> Short URLs make denser QR patterns scan faster, but if the redirect introduces a tracking layer, you lose privacy and add a failure point. For permanent codes, the destination URL should be exactly what you want the user to see, with no redirect chain in between.</p>
          </div>

          {/* EXISTING: FAQ (kept — already strong at 10) */}
          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>What types of data can QR codes store?</h3>
                <p>QR codes can store various data types including URLs (up to 4,296 characters), plain text, contact information (vCard format), WiFi credentials, calendar events, geographic coordinates, and more. The storage capacity depends on the data type and error correction level.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference between PNG and SVG formats?</h3>
                <p>PNG is a raster format with fixed resolution, ideal for digital displays and web use. SVG is a vector format that can be scaled infinitely without quality loss, making it perfect for print materials, large displays, and professional design work.</p>
              </div>
              <div className="faq-item">
                <h3>How do error correction levels work?</h3>
                <p>Error correction adds redundant data that allows QR codes to remain readable when damaged. Low (7%) works for perfect conditions, Medium (15%) for general use, Quartile (25%) for outdoor environments, and High (30%) for industrial applications where codes might get damaged.</p>
              </div>
              <div className="faq-item">
                <h3>Can I customize QR code colors?</h3>
                <p>Yes, you can customize foreground and background colors, but ensure sufficient contrast for reliable scanning. Dark foreground on light background works best. Avoid using similar colors or low contrast combinations that may prevent scanning.</p>
              </div>
              <div className="faq-item">
                <h3>Are these QR codes free to use?</h3>
                <p>Yes, all QR codes generated by our tool are completely free to use for personal and commercial purposes. There are no restrictions on usage, and no attribution is required. The QR code patent has expired, making the technology free for everyone.</p>
              </div>
              <div className="faq-item">
                <h3>Do QR codes expire?</h3>
                <p>The static QR codes created here never expire. Because the data is stored directly inside the code image, there is no server or link in the middle that could be switched off, so the code keeps working indefinitely. Only dynamic QR codes from paid providers can expire, usually when the subscription ends or the account is closed.</p>
              </div>
              <div className="faq-item">
                <h3>Can I create a WiFi or vCard QR code with this tool?</h3>
                <p>Yes. This generator encodes any text, so you can create WiFi, contact (vCard), email, phone, SMS, and location QR codes by pasting the correctly formatted text into the input field. See the "QR Code Types You Can Create" section above for the exact format to copy for each type.</p>
              </div>
              <div className="faq-item">
                <h3>Can I track how many times my QR code is scanned?</h3>
                <p>No. Scan tracking requires a dynamic QR code that routes each scan through a provider's server. Our tool creates static QR codes that work offline and store no data, so there is nothing to track. If analytics are essential for a campaign, you would need a paid dynamic QR service.</p>
              </div>
              <div className="faq-item">
                <h3>How much data can a QR code store?</h3>
                <p>A single QR code can hold up to 4,296 alphanumeric characters, 7,089 numeric digits, or 2,953 bytes of binary data. In practice, shorter content is better: less data means a simpler, less dense pattern that scans faster and more reliably, especially in print.</p>
              </div>
              <div className="faq-item">
                <h3>Is signup or an account required?</h3>
                <p>No. There is no signup, login, or account needed. Everything runs directly in your browser, your data never leaves your device, and you can generate unlimited QR codes for free.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/password/" className="related-tool">
                <div className="tool-icon">Password Generator</div>
                <div className="tool-description">Create secure passwords for QR code-protected accounts</div>
              </Link>
              <Link to="/text-to-speech/" className="related-tool">
                <div className="tool-icon">Text to Speech</div>
                <div className="tool-description">Convert QR code content to audio for accessibility</div>
              </Link>
              <Link to="/json-formatter/" className="related-tool">
                <div className="tool-icon">JSON Formatter</div>
                <div className="tool-description">Format structured data for QR code encoding</div>
              </Link>
              <Link to="/blog/what-is-qr-code-and-how-to-use-it/" className="related-tool">
                <div className="tool-icon">What is a QR Code and How to Use It</div>
                <div className="tool-description">Complete guide to QR code types and best practices</div>
              </Link>
              <Link to="/blog/how-to-make-a-wifi-qr-code/" className="related-tool">
                <div className="tool-icon">How to Make a WiFi QR Code</div>
                <div className="tool-description">Step-by-step guide to sharing WiFi access by scan</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default QrGeneratorPage